/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';
import OverviewTab from '@/components/tabs/dashboard/OverviewTab';
import StatisticsTab from '@/components/tabs/dashboard/StatisticsTab';
import ThermalConflictTab from '@/components/tabs/dashboard/ThermalConflictTab';

interface DashboardGridLayoutsTabProps {
    theme: 'light' | 'dark';
}

const OVERVIEW_TAB_EXAMPLE_CODE = `<DashboardGrid
    desktopCols={["2fr", "1fr"]}
    desktopRows={["1fr", "1fr", "1fr", "1fr"]}
    breakpoint={"2xl"}
    aria-label={t('Overview dashboard')}
    mobileMinHeight={"150px"}
>
    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box1</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel" desktopRowSpan={3} mobileMinHeight={"500px"}>
        <span>Box2</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box3</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box4</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box5</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box6</span>
    </DashboardGrid.Item>
</DashboardGrid>`;

const STATISTICS_TAB_EXAMPLE_CODE = `<DashboardGrid
    desktopCols={4}
    desktopRows={4}
    breakpoint={"2xl"}
    aria-label={t('Statistics dashboard')}
>
    <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={2}>
        <span>Box1</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={2}>
        <span>Box2</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={2}>
        <span>Box3</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={2}>
        <span>Box4</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={2}>
        <span>Box5</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={2}>
        <span>Box6</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={2}>
        <span>Box7</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box8</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box9</span>
    </DashboardGrid.Item>
</DashboardGrid>`;

const THERMAL_CONFLICT_TAB_EXAMPLE_CODE = `<DashboardGrid
    desktopCols={4}
    desktopRows={["160px", "1fr"]}
    breakpoint={"2xl"}
    aria-label={t("Thermal conflict dashboard")}
>
    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box1</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box2</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box3</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel">
        <span>Box4</span>
    </DashboardGrid.Item>

    <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={4}>
        <span>Box5</span>
    </DashboardGrid.Item>
</DashboardGrid>`;

function DashboardGridLayoutsTab({ theme }: DashboardGridLayoutsTabProps) {
    return (
        <div className="layouts-tab">
            <LayoutSectionHeader sectionKey="dashboard-grid" />

            <section className="layouts-section">
                <h3 className="text-xl font-semibold">OverviewTab example</h3>

                <h4 className="tracking-wide">Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button type="button" className="tab tab-active">
                            JSX
                        </button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={OVERVIEW_TAB_EXAMPLE_CODE}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>

                <h4 className="tracking-wide">Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel items-stretch justify-stretch p-6 overflow-visible">
                    <div className="w-full grid min-h-[500px] items-stretch">
                        <OverviewTab />
                    </div>
                </section>
            </section>

            <section className="layouts-section">
                <h3 className="text-xl font-semibold">StatisticsTab example</h3>

                <h4 className="tracking-wide">Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button type="button" className="tab tab-active">
                            JSX
                        </button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={STATISTICS_TAB_EXAMPLE_CODE}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>

                <h4 className="tracking-wide">Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel items-stretch justify-stretch p-6 overflow-visible">
                    <div className="w-full grid min-h-[500px] items-stretch">
                        <StatisticsTab />
                    </div>
                </section>
            </section>

            <section className="layouts-section">
                <h3 className="text-xl font-semibold">ThermalConflictTab example</h3>

                <h4 className="tracking-wide">Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button type="button" className="tab tab-active">
                            JSX
                        </button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={THERMAL_CONFLICT_TAB_EXAMPLE_CODE}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>

                <h4 className="tracking-wide">Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel items-stretch justify-stretch p-6 overflow-visible">
                    <div className="w-full grid min-h-[500px] items-stretch">
                        <ThermalConflictTab />
                    </div>
                </section>
            </section>
        </div>
    );
}

export default DashboardGridLayoutsTab;
